/**
 * Financial helpers used across the Estimate module.
 *
 * All math is decimal-safe on the FRONTEND to give the user instant feedback,
 * but the AUTHORITATIVE totals come from the backend on every save.
 */

export const inr = (v) => {
  const n = Number(v || 0);
  if (Number.isNaN(n)) return "₹0.00";
  const fixed = n.toFixed(2);
  const [whole, dec] = fixed.split(".");
  // Indian grouping: last 3 then groups of 2
  const lastThree = whole.slice(-3);
  const rest = whole.slice(0, -3);
  const grouped = rest ? rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + lastThree : lastThree;
  return `₹${grouped}.${dec}`;
};

export function calcLine(l, taxMode = "exclusive") {
  const q = Number(l.qty || 0);
  const up = Number(l.unit_price || 0);
  const base = q * up;
  let discount = 0;
  if ((l.discount_type || "amount") === "percent") {
    discount = base * Number(l.discount_value || 0) / 100;
  } else {
    discount = Number(l.discount_value || 0);
  }
  if (discount > base) discount = base;
  const taxable = base - discount;
  const tp = Number(l.tax_pct || 0);
  let tax = 0, taxableEx = taxable, lineTotal = taxable;
  if (taxMode === "inclusive") {
    tax = taxable - (taxable / (1 + tp / 100));
    taxableEx = taxable - tax;
    lineTotal = taxable;
  } else {
    tax = taxable * tp / 100;
    taxableEx = taxable;
    lineTotal = taxable + tax;
  }
  return {
    base: round2(base),
    discount: round2(discount),
    taxable: round2(taxableEx),
    tax_amount: round2(tax),
    line_total: round2(lineTotal),
  };
}

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

export function calcEstimate(doc) {
  const taxMode = doc.tax_mode || "exclusive";
  const process = (lines) => {
    let subtotal = 0, tax = 0;
    for (const l of lines || []) {
      const c = calcLine(l, taxMode);
      if (l.optional) continue;
      subtotal += c.taxable;
      tax += c.tax_amount;
    }
    return { subtotal, tax };
  };
  const parts = process(doc.parts);
  const labor = process(doc.labor);
  const other = process(doc.other_charges);
  const gross = parts.subtotal + labor.subtotal + other.subtotal;
  const est_disc_v = Number(doc.discount_value || 0);
  const overallDiscount = Math.min(
    (doc.discount_type || "amount") === "percent" ? gross * est_disc_v / 100 : est_disc_v,
    gross,
  );
  const scale = gross > 0 ? (gross - overallDiscount) / gross : 1;
  const totalTax = (parts.tax + labor.tax + other.tax) * scale;
  const taxable = gross - overallDiscount;
  const isInter = !!doc.is_interstate;
  const cgst = isInter ? 0 : totalTax / 2;
  const sgst = isInter ? 0 : totalTax / 2;
  const igst = isInter ? totalTax : 0;
  const pre = taxable + totalTax;
  const grand = Math.round(pre);
  const rounding = grand - pre;
  return {
    parts_subtotal: round2(parts.subtotal),
    labor_subtotal: round2(labor.subtotal),
    charges_subtotal: round2(other.subtotal),
    gross_subtotal: round2(gross),
    overall_discount: round2(overallDiscount),
    taxable_value: round2(taxable),
    cgst: round2(cgst),
    sgst: round2(sgst),
    igst: round2(igst),
    total_tax: round2(totalTax),
    rounding_adjustment: round2(rounding),
    grand_total: round2(grand),
  };
}

export const emptyEstimate = () => ({
  source: "standalone",
  customer_snapshot: { name: "", phone: "", email: "", address: "" },
  vehicle_snapshot: {},
  complaints: [],
  parts: [],
  labor: [],
  other_charges: [],
  discount_type: "amount",
  discount_value: 0,
  discount_reason: "",
  tax_mode: "exclusive",
  is_interstate: false,
  estimate_date: new Date().toISOString().slice(0, 10),
  valid_until: new Date(Date.now() + 7 * 24 * 3600 * 1000).toISOString().slice(0, 10),
  status: "draft",
  terms: [],
  customer_notes: "",
  internal_notes: "",
});

export const STATUS_META = {
  draft: { label: "Draft", chip: "bg-[#66708515] text-[#667085]" },
  prepared: { label: "Prepared", chip: "bg-[#457b9d]/15 text-[#457b9d]" },
  expired: { label: "Expired", chip: "bg-[#F79009]/15 text-[#F79009]" },
  converted: { label: "Converted", chip: "bg-[#12B76A]/15 text-[#12B76A]" },
  cancelled: { label: "Cancelled", chip: "bg-[#e63946]/15 text-[#e63946]" },
};
